import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ServiceReserveStatus, ServiceViewType } from "@/apis/clients";
import { ApiValidationError } from "@/apis/errors";
import EditorFrame from "@/components/editors/EditorFrame";
import CommonButtonContent from "@/components/editors/template-blocks/cta-button/cta-button-items/CommonButtonContent";
import { CommonModal } from "@/components/molecules/modals/CommonModal";
import { useSubmitForm } from "@/hooks/useSubmitForm";
import { useLoading } from "@/states/loading/operations";
import { ButtonContentType } from "@/states/page-editor/template-parts/common";
import {
  useHandleSubmitForm,
  useSetIsSummited,
} from "@/states/service-form/operations";
import {
  useCannotOrderReasons,
  useFormData,
  useIsAgreePolicy,
  useResetServiceFormUiState,
  useServiceFormUiState,
  useServiceInfo,
} from "@/states/service-form/selectors";
import { scrollToElement } from "@/utils/common";

interface Props {
  buttonContent: ButtonContentType;
  formPath?: string;
  confirmPath?: string;
}
interface QueryParams {
  lpUuid?: string;
  simpleUuid?: string;
  iframeUuid?: string;
}
export default function CommonButtonTemplate({
  buttonContent,
  formPath,
  confirmPath,
}: Props) {
  const withLoading = useLoading();
  const service = useServiceInfo();
  const formData = useFormData();
  const serviceFormUiState = useServiceFormUiState();
  const { canSubmitOnLp } = useSubmitForm();
  const cannotOrderReasons = useCannotOrderReasons();
  const isAgreePolicy = useIsAgreePolicy();
  const setIsSummited = useSetIsSummited();
  const resetServiceFormUiState = useResetServiceFormUiState();

  const router = useRouter();

  const [submitErros, setSubmitErros] = useState<string[]>([]);
  const [canNextPage, setNextPage] = useState(
    formPath && canSubmitOnLp ? true : canSubmitOnLp,
  );
  const [nextLink, setNextLink] = useState(
    service?.type === "calendar" ? "" : formPath ? formPath : confirmPath,
  );

  // const isShowTerm = service?.viewType === ServiceViewType.FORM;
  const isShowTerm = false;
  const canButtonSubmit = true;

  useEffect(() => {
    const canNext = canSubmitOnLp && (!isShowTerm || isAgreePolicy);
    setNextPage(formPath && canNext ? true : canNext);
    setNextLink(
      service?.type === "calendar" ? "" : formPath ? formPath : confirmPath,
    );
  }, [canSubmitOnLp, confirmPath, formPath, isAgreePolicy, isShowTerm, service?.type]);

  const queryParams: QueryParams | undefined = useMemo(() => {
    if (!router.isReady) {
      return undefined;
    }
    const getLpId = (query: ParsedUrlQuery) => {
      const { uuid } = query;
      if (uuid === undefined || typeof uuid !== "string") {
        return undefined;
      }
      return uuid;
    };

    const getSimpleId = (query: ParsedUrlQuery) => {
      const { simpleId } = query;
      if (simpleId === undefined || typeof simpleId !== "string") {
        return undefined;
      }
      return simpleId;
    };

    const getIframeId = (query: ParsedUrlQuery) => {
      const { iframeId } = query;
      if (iframeId === undefined || typeof iframeId !== "string") {
        return undefined;
      }
      return iframeId;
    };

    return {
      lpUuid: getLpId(router.query),
      simpleUuid: getSimpleId(router.query),
      iframeUuid: getIframeId(router.query),
    };
  }, [router.isReady, router.query]);
  const isPaymentFormFlg = useCallback(() => {
    if (!service) return false;

    if (service.type === "calendar") {
      if (serviceFormUiState.status === "Loaded") {
        if (!serviceFormUiState.inputs.service) return false;
        const serviceMenu = service.menus.filter(
          (menu) => menu.id === serviceFormUiState.inputs.service.serviceMenuId,
        );
        if (serviceMenu.length > 0 && serviceMenu[0].price > 0) return true;
      }
      return false;
    }

    if (service.type === "event-seminar" || service.type === "form") {
      let playFlagResult = service.details.map((detail) => {
        return detail.price && detail.price > 0;
      });
      if (service.reserveStatus === ServiceReserveStatus.ALL) {
        playFlagResult = service.details.map((detail) => {
          return detail.price && detail.price > 0;
        });
      }

      return playFlagResult.includes(true);
    }

    return false;
  }, [service]);
  const formUuid = useMemo(() => {
    const { uuid: formUuid } = router.query;
    if (formUuid === undefined || typeof formUuid !== "string") {
      return undefined;
    }
    return formUuid;
  }, [router.query]);
  useEffect(() => {
    if (
      (service?.type === "event-seminar" || service?.type === "form") &&
      isPaymentFormFlg()
    ) {
      let query = "";
      let path = "";
      if (queryParams?.lpUuid) {
        query = `lpId=${queryParams.lpUuid}&from=${service?.viewType}`;
      } else if (queryParams?.iframeUuid) {
        query = `iframeId=${queryParams.iframeUuid}&from=${service?.viewType}`;
      } else {
        query = `from=${service?.viewType}`;
      }
      if (service?.viewType === ServiceViewType.FORM) {
        path = `/form/${formUuid || ""}/payment?${query}`;
        setNextLink(path);
        return;
      }
    } else if (service?.type === "calendar") {
      // Calendar service CTA button on page scrolls to menu instead of navigating
      setNextLink("");
    } else if (
      service?.type === "survey" ||
      service?.type === "document-request" ||
      service?.type === "email-newsletter"
    ) {
      setNextLink(formPath || "");
    }
  }, [
    formUuid,
    isPaymentFormFlg,
    queryParams?.lpUuid,
    queryParams?.simpleUuid,
    queryParams?.iframeUuid,
    service,
    canSubmitOnLp,
    canNextPage,
    confirmPath,
    formPath,
    router,
  ]);

  const disabled = useMemo(() => {
    if (isShowTerm) {
      return false;
    }
    return cannotOrderReasons.length > 0;
  }, [cannotOrderReasons.length, isShowTerm]);

  const goToErrorText = useCallback(() => {
    window.dispatchEvent(new Event("button.handleFormSubmit"));
    if (!canNextPage) {
      const errorElements = document.getElementsByClassName("form-error-text");
      if (errorElements && errorElements[0]) {
        scrollToElement(errorElements[0], "center");
      } else if (isShowTerm && !isAgreePolicy) {
        const termElements = document.getElementById("term-policy");
        scrollToElement(termElements, "center");
      }
      return true;
    }
    return false;
  }, [canNextPage, isAgreePolicy, isShowTerm]);

  const handleGoToForm = () => {
    // Service Calendar
    // Scroll into menu calendar
    const menuCalendarElement = document.getElementById(
      "service-menu-calendar-template",
    );
    if (menuCalendarElement) {
      scrollToElement(menuCalendarElement, "start");
      return;
    }

    // Other Service
    // Scroll into require menu
    const requireMenuElement = document.getElementById("service-menu-template");
    if (
      requireMenuElement &&
      service?.reserveStatus !== ServiceReserveStatus.ALL &&
      service?.details &&
      service?.details.length > 1
    ) {
      scrollToElement(requireMenuElement, "start");
      return;
    }
    // Scroll into form
    const formElement = document.getElementById("service-form-template");
    if (formElement) {
      scrollToElement(formElement, "start");
      return;
    }
    // Scroll into menu
    const menuElement = document.getElementById("service-menu-template");
    if (menuElement) {
      scrollToElement(menuElement, "start");
      return;
    }
  };
  const handleSubmitService = () => {
    setIsSummited({ ...formData, isSubmit: true });
    setTimeout(() => {
      if (!goToErrorText()) {
        void onClickSubmit();
      }
    }, 100);
  };
  const handleSubmitForm = useHandleSubmitForm();
  const onClickSubmit = useCallback(async () => {
    await withLoading(async () => {
      try {
        const result = await handleSubmitForm(formUuid);
        let thanksUrl = `/thanks/${result.thanksUuid}?order_uuid=${
          result.orderUuid
        }&lp_uiid=${queryParams?.lpUuid || ""}`;

        if (
          !(service?.type === "survey" || service?.type === "email-newsletter")
        ) {
          thanksUrl += `&reservation_code=${result.id}`;
        }
        await router.push(thanksUrl);
        resetServiceFormUiState();
      } catch (e) {
        if (e instanceof ApiValidationError) {
          for (const key in e.errors) {
            const err = e.errors[key];
            setSubmitErros(err);
          }
          return;
        }
      }
    });
  }, [formUuid, router, withLoading, handleSubmitForm]);

  return (
    <>
      <EditorFrame noBorder isNoPadding>
        {cannotOrderReasons.length > 0 &&
          service?.viewType === ServiceViewType.BUTTON && (
            <div className="">
              {cannotOrderReasons.map((reason, index) => (
                <p key={index} className="text-xl text-center text-accent">
                  {reason}
                </p>
              ))}
            </div>
          )}

        <CommonButtonContent
          buttonContent={buttonContent}
          formPath={canButtonSubmit ? (canNextPage ? nextLink : "") : undefined}
          onClick={handleGoToForm}
          disabled={disabled}
        />
      </EditorFrame>
      <CommonModal
        isOpen={submitErros.length > 0}
        close={() => setSubmitErros([])}
        submit={() => setSubmitErros([])}
        title={"申し込みできませんでした"}
        btnText="閉じる"
      >
        {submitErros?.map((message, index) => (
          <div key={`error-message-${index}`}>{message}</div>
        ))}
      </CommonModal>
    </>
  );
}
